import {
	Box,
	Button,
	Container,
	DialogContent,
	Grid,
	IconButton,
	InputAdornment,
	makeStyles,
} from "@material-ui/core";
import AddBoxIcon from "@material-ui/icons/AddBox";
import { verifyError } from "Components/Common/helpers/verifyError";
import { useComputedCompany } from "Components/Company/hooks/useComputedCompany";
import { useModalController } from "Components/Modal/Modal";
import ModalTitle from "Components/Modal/ModalTitle";
import { useSchedule } from "Components/Schedule/hooks/useSchedule";
import { createScheduleResource } from "Components/ScheduleResource/ScheduleResourceHelper";
import { useScheduleResourceModal } from "Components/ScheduleResource/hooks/useScheduleResourceModal";
import { useScheduleResources } from "Components/ScheduleResource/hooks/useScheduleResources";
import { useTutorialModal } from "Components/Tutorial/hooks/useTutorialModal";
import { color, px } from "Configs/theme";
import TextField from "Fields/TextField";
import { getColorRgbaFromHex } from "Helpers/ColorHelper";
import { useRedirect } from "Helpers/RedirectHelper";
import { useApp } from "Providers/AppProvider";
import { useTranslation } from "Providers/TranslationProvider";
import {
	Schedule,
	ScheduleGroup,
	ScheduleGroupItem,
	ScheduleResource,
} from "domain-entities";
import { useSnackbar } from "material-ui-snackbar-provider";
import React, {
	FC,
	Fragment,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { useForm } from "react-hook-form";
import { ReactSortable } from "react-sortablejs";
import { v4 } from "uuid";
import * as yup from "yup";
import {
	getCleanedScheduleGroups,
	getRemainingItems,
} from "../ScheduleGroup/ScheduleGroupHelper";
import ScheduleGroupItemList, {
	ANIMATION_DURATION,
} from "../ScheduleGroup/ScheduleGroupItemList";
import ScheduleGroupModal from "../ScheduleGroup/ScheduleGroupModal";
import { useTeamsModal } from "../Team/hooks/useTeamsModal";

interface Props {
	schedule: Schedule;
	scheduleResource?: ScheduleResource;
}

interface FormValues {
	scheduleGroupName: string;
}

const ScheduleForm: FC<Props> = (props) => {
	const { t } = useTranslation();
	const { isNative } = useApp();
	const isFirstGroupsRender = useRef(true);
	const validMembers = useComputedCompany((state) => state.validMembers);
	const redirect = useRedirect();
	const scheduleResources = useScheduleResources((state) => state.items);
	const [scheduleGroups, setScheduleGroups] = useState<ScheduleGroup[]>(
		getCleanedScheduleGroups(props.schedule.groups)
	);
	const scheduleGroupItems = scheduleGroups
		.map((scheduleGroups) => scheduleGroups.items)
		.flat();
	const scheduleGroupItemsRef = useRef(scheduleGroupItems);
	const [memberItems, setMemberItems] = useState(
		getRemainingItems(validMembers, "userId", scheduleGroupItems)
	);
	const [resourceItems, setResourceItems] = useState(
		getRemainingItems(scheduleResources, "resourceId", scheduleGroupItems)
	);

	useEffect(() => {
		scheduleGroupItemsRef.current = scheduleGroupItems;
	}, [scheduleGroupItems]);

	useEffect(() => {
		setResourceItems(
			getRemainingItems(
				scheduleResources,
				"resourceId",
				scheduleGroupItemsRef.current
			)
		);
	}, [scheduleResources]);

	const snackbar = useSnackbar();
	const classes = useStyles();
	const scheduleGroupMC = useModalController<ScheduleGroup>();

	const openScheduleResource = useCallback(
		(scheduleGroupItem: ScheduleGroupItem) => {
			const scheduleResource = scheduleResources.find(
				(scheduleResource) =>
					scheduleResource.id === scheduleGroupItem.resourceId
			);

			if (scheduleResource) {
				useScheduleResourceModal.getState().openWithItem(scheduleResource);
			}
		},
		[scheduleResources]
	);

	const defaultValues: FormValues = {
		scheduleGroupName: "",
	};

	const validationSchema = yup.object().shape({
		scheduleGroupName: yup.string().required(
			t("Common_Form_Error_MandatoryField", {
				field: t("ScheduleGroupsForm_Label_ScheduleGroupName"),
			})
		),
	});

	const form = useForm<FormValues>({ validationSchema, defaultValues });
	const { control } = form;

	const submit = useCallback(
		async (cleanedScheduleGroups: ScheduleGroup[]) => {
			try {
				if (useSchedule.getState().item) {
					await useSchedule.getState().updateItem({
						groups: cleanedScheduleGroups,
					});
				} else {
					await useSchedule.getState().setItem({
						id: props.schedule.id,
						groups: cleanedScheduleGroups,
					});
				}
			} catch (error) {
				snackbar.showMessage(verifyError(error).message);
			}
		},
		[snackbar, props]
	);

	const nextCleanedScheduleGroups = getCleanedScheduleGroups(scheduleGroups);
	const nextScheduleGroupsJson = JSON.stringify(nextCleanedScheduleGroups);
	const previousScheduleGroupsJsonRef = useRef(nextScheduleGroupsJson);

	useEffect(() => {
		if (previousScheduleGroupsJsonRef.current !== nextScheduleGroupsJson) {
			submit(nextCleanedScheduleGroups);
		}

		previousScheduleGroupsJsonRef.current = nextScheduleGroupsJson;
	}, [
		submit,
		scheduleGroups,
		nextScheduleGroupsJson,
		nextCleanedScheduleGroups,
	]);

	async function onCreateScheduleGroup(formValues: FormValues) {
		if (formValues.scheduleGroupName) {
			const nextScheduleGroups = [
				{ id: v4(), items: [], name: formValues.scheduleGroupName },
				...scheduleGroups,
			];

			setScheduleGroups(nextScheduleGroups);
			form.setValue("scheduleGroupName", "");

			setTimeout(() => {
				useTutorialModal
					.getState()
					.openWithItem("dragAndDropScheduleGroupItem");

				if (nextScheduleGroups.length > 1) {
					useTutorialModal.getState().openWithItem("sortScheduleGroups");
				}
			}, 200);
		}
	}

	function renderGroups() {
		return (
			<ReactSortable
				tag="div"
				list={scheduleGroups}
				group={{
					name: "groups",
					put: false,
					pull: false,
				}}
				ghostClass={classes.ghost}
				setList={(nextGroups) => {
					if (isFirstGroupsRender.current) {
						isFirstGroupsRender.current = false;
						setTimeout(() => setScheduleGroups(nextGroups));
					} else {
						setScheduleGroups(nextGroups);
					}
				}}
				swapThreshold={1}
				animation={ANIMATION_DURATION}
			>
				{scheduleGroups.map((scheduleGroup) => (
					<Box key={scheduleGroup.id} className={classes.group}>
						{renderItems(scheduleGroup)}
					</Box>
				))}
			</ReactSortable>
		);
	}

	function renderItems(scheduleGroup: ScheduleGroup) {
		return (
			<ScheduleGroupItemList
				key={scheduleGroup.id}
				name={scheduleGroup.name}
				items={scheduleGroup.items}
				emptyPlaceholder={t("ScheduleGroupsForm_EmptyPlaceholder_Group")}
				setList={(nextItems) => {
					scheduleGroup.items = nextItems;
					setScheduleGroups([...scheduleGroups]);
				}}
				group={{
					name: "items",
					put: ["members", "resources", "items"],
					pull: ["members", "resources", "items"],
				}}
				onClickEdit={() => scheduleGroupMC.open(scheduleGroup)}
				onItemAction={(scheduleGroupItem) => {
					scheduleGroup.items = scheduleGroup.items.filter(
						(item) => item !== scheduleGroupItem
					);

					if (scheduleGroupItem.userId) {
						setMemberItems([...memberItems, scheduleGroupItem]);
					} else if (scheduleGroupItem.resourceId) {
						setResourceItems([...resourceItems, scheduleGroupItem]);
					}

					setScheduleGroups([...scheduleGroups]);
				}}
				isSortable
			/>
		);
	}

	function renderMembers() {
		return (
			<ScheduleGroupItemList
				name={t("ScheduleGroupsForm_GroupTitle_Members")}
				items={memberItems}
				setList={setMemberItems}
				emptyPlaceholder={t("ScheduleGroupsForm_EmptyPlaceholder_Members")}
				actionLabel={t("ScheduleGroupsForm_GroupActionLabel_Members")}
				onAction={() => {
					setTimeout(() => {
						redirect("settings/company-employees");
					}, 100);
				}}
				group={{
					name: "members",
					put: (to, from, dragEl) => {
						const draggedId = dragEl?.getAttribute("data-id") || "";
						const draggedItem = scheduleGroupItemsRef.current.find(
							(item) => item.id === draggedId
						);

						return draggedItem?.userId ? ["items"] : false;
					},
					pull: ["items"],
				}}
			/>
		);
	}

	function renderResources() {
		return (
			<ScheduleGroupItemList
				name={t("ScheduleGroupsForm_GroupTitle_Resources")}
				items={resourceItems}
				setList={setResourceItems}
				emptyPlaceholder={t("ScheduleGroupsForm_EmptyPlaceholder_Resources")}
				actionLabel={t("ScheduleGroupsForm_GroupActionLabel_Resources")}
				onAction={() =>
					useScheduleResourceModal
						.getState()
						.openWithItem(createScheduleResource(), true)
				}
				onItemAction={openScheduleResource}
				isItemActionEdit
				group={{
					name: "resources",
					put: (to, from, dragEl) => {
						const draggedId = dragEl?.getAttribute("data-id") || "";
						const draggedItem = scheduleGroupItemsRef.current.find(
							(item) => item.id === draggedId
						);

						return draggedItem?.resourceId ? ["items"] : false;
					},
					pull: ["items"],
				}}
			/>
		);
	}

	return (
		<Fragment>
			<Container className={classes.root} maxWidth="lg">
				<ModalTitle title={t("ScheduleGroupsForm_Title")}>
					<Button
						onClick={() => useTeamsModal.getState().close()}
						variant="contained"
						color="primary"
					>
						{t("Common_Dialog_Done")}
					</Button>
				</ModalTitle>
				<DialogContent className={classes.dialogContent}>
					<Grid container spacing={4}>
						<Grid item xs={4} className={classes.scrollableGrid}>
							<form onSubmit={form.handleSubmit(onCreateScheduleGroup)}>
								<Box mb={px.l}>
									<TextField
										control={control}
										label={t("ScheduleGroupsForm_Label_ScheduleGroupName")}
										name="scheduleGroupName"
										autoFocus={!isNative}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														type="submit"
														onClick={form.handleSubmit(onCreateScheduleGroup)}
													>
														<AddBoxIcon color="primary" />
													</IconButton>
												</InputAdornment>
											),
										}}
									/>
								</Box>
							</form>
							{renderGroups()}
						</Grid>
						<Grid item xs={4} className={classes.scrollableGrid}>
							{renderMembers()}
						</Grid>
						<Grid item xs={4} className={classes.scrollableGrid}>
							{renderResources()}
						</Grid>
					</Grid>
				</DialogContent>
			</Container>
			<ScheduleGroupModal
				modalController={scheduleGroupMC}
				remove={(scheduleGroup) => {
					const index = scheduleGroups.indexOf(scheduleGroup);
					if (index > -1) {
						scheduleGroups.splice(index, 1);
						setScheduleGroups([...scheduleGroups]);
						scheduleGroup.items.forEach((scheduleGroupItem) => {
							if (scheduleGroupItem.userId) {
								memberItems.push(scheduleGroupItem);
							} else if (scheduleGroupItem.resourceId) {
								resourceItems.push(scheduleGroupItem);
							}
						});

						setMemberItems([...memberItems]);
						setResourceItems([...resourceItems]);
					}
				}}
			/>
		</Fragment>
	);
};

export default ScheduleForm;

const useStyles = makeStyles({
	root: {
		display: "flex",
		flexDirection: "column",
		height: "100%",
	},
	dialogContent: {
		display: "flex",
		flex: 1,
		overflowY: "hidden",
	},
	scrollableGrid: {
		overflowY: "scroll",
		height: "100%",
	},
	group: {
		marginBottom: px.xl,
	},
	ghost: {
		backgroundColor: getColorRgbaFromHex(color.primary, 0.12),
		borderRadius: px.xs,
		"& > *": {
			visibility: "hidden",
		},
	},
});
